import { createAsyncThunk } from '@reduxjs/toolkit';

import { setLoading } from 'src/components/common/loading/loadingSlice';
import {
	IStudyAssignment,
	ICreateStudyAssignmentThunkArgs,
	IUpdateStudyAssignmentThunkArgs,
	IGetCurrentUsersStudyAssignmentResponse,
	IGetCurrentUsersStudyAssignmentThunkArgs,
	IGetNextStudyAssignmentResponse,
	IGetNextStudyAssignmentThunkArgs,
} from 'src/features/study_assignment/domain/types';
import { studyAssignmentApi } from 'src/features/study_assignment/infrastructure/api';
import { handleThunkError } from 'src/utils/handleThunkError';

export const STUDY_ASSIGNMENT_LOADING_KEY = 'study_assignment';

export const createStudyAssignment = createAsyncThunk<
	IStudyAssignment,
	ICreateStudyAssignmentThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'study_assignment/createStudyAssignment',
	async (
		{ request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: STUDY_ASSIGNMENT_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await studyAssignmentApi.createStudyAssignment(
				request,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to create study assignment',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: STUDY_ASSIGNMENT_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const updateStudyAssignment = createAsyncThunk<
	IStudyAssignment,
	IUpdateStudyAssignmentThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'study_assignment/updateStudyAssignment',
	async (
		{ request, studyAssignmentId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: STUDY_ASSIGNMENT_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await studyAssignmentApi.updateStudyAssignment(
				studyAssignmentId,
				request,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to update study assignment',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: STUDY_ASSIGNMENT_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const getCurrentUsersStudyAssignment = createAsyncThunk<
	IGetCurrentUsersStudyAssignmentResponse,
	IGetCurrentUsersStudyAssignmentThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'study_assignment/getCurrentUsersStudyAssignment',
	async (
		{ studyId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: STUDY_ASSIGNMENT_LOADING_KEY,
					loading: true,
				}),
			);
			const response =
				await studyAssignmentApi.getCurrentUserStudyAssignment(
					studyId,
					siteSlug,
				);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				"Failed to fetch user's study assignment",
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: STUDY_ASSIGNMENT_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const getNextStudyAssignment = createAsyncThunk<
	IGetNextStudyAssignmentResponse,
	IGetNextStudyAssignmentThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'study_assignment/getNextStudyAssignment',
	async ({ siteSlug, onSuccess, onError }, { dispatch, rejectWithValue }) => {
		try {
			dispatch(
				setLoading({
					key: STUDY_ASSIGNMENT_LOADING_KEY,
					loading: true,
				}),
			);
			const response =
				await studyAssignmentApi.getNextStudyAssignment(siteSlug);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to fetch next study assignment',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: STUDY_ASSIGNMENT_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);
